import { useCallback, useEffect, useState } from 'react'
import { CustomerModelType } from '@teinor/erp/types/company/customer'
import { faPlus } from '@fortawesome/pro-solid-svg-icons'
import { twinFetchPostJSON } from '../../../../../../utils/globals/data'
import { getConfigParam, getUserInfo, getUserPermissions } from '../../../../../../utils/reducers/getters'
import PermissionChecker from '../../../../../../baseComponents/PermissionChecker'
import TwinIcon, { TwinIconToolTip } from '../../../../../../baseComponents/TwinIcon'
import TwinTrans from '../../../../../../baseComponents/TwinTrans'
import useTwinTranslation from '../../../../../../utils/hooks/useTwinTranslation'
import { TaskModelType } from '@teinor/erp/types/company/task'
import { CustomRenderTasksFinished } from '../../../../../Task/TaskScreenTable/Subcomponents'
import { addBottomNavAppElement } from '../../../../../../utils/reducers/reduxDispatch'
import { dictionaryComplexFromJsonArr, TwinDictionary } from '../../../../../../utils/globals/dictionary'
import { listenSocket, unlistenSocket } from '../../../../../../utils/globals/socket'
import TwinToolTip from '../../../../../../baseComponents/TwinToolTip'
import { faCalendar, faInfoCircle } from '@fortawesome/pro-regular-svg-icons'
import { displayDate, displayDateTime } from '../../../../../../utils/globals/date'
import { OnSubmit } from '../../../../../../forms/TwinForm/types'
import { CustomerTypesInversedKeys } from '@teinor/erp/types/company/customer/customerTypes'
import { ComponentWithPermissions } from '../../../../../../baseComponents/CreateRoutering/types'
import { CustomerDashboardSeeAllButton } from '../../SpecificComponents'
import { faEyeSlash } from '@fortawesome/pro-light-svg-icons'
import CustomSelectTaskContactInfoRow from '../../../../../Task/Components/CustomSelectTaskContactInfo'

interface CustomerRecentTasksDashboardProps extends CustomerRecentTasksDashboardLogicProps { 
    title: string
}

const CustomerRecentTasksDashboard: React.FC<CustomerRecentTasksDashboardProps> = ({ allRowData, title }) => {
    const { data, getData, projects } = useCustomerRecentTasksDashboardLogic({ allRowData })
    const CustomerProjectId = projects?.[Object.keys(projects)[0]]?.id
    const userPermissions = getUserPermissions('customer.profile.tasks')
    return (
        <div className='flex flex-auto flex-col gap-10'>
            <div className='flex flex-wrap flex-row justify-between gap-5'>
                <h3>{title}</h3>
                <AddTaskButton userPermissions={userPermissions || { n: 4, permission: 'delete' }} onSubmit={getData} CustomerProjectId={CustomerProjectId} />
            </div>
            <CustomerTasksList customerType={allRowData?.customer_type} tasks={data} />
        </div>
    )
}

interface CustomerRecentTasksDashboardLogicProps {
    allRowData: CustomerModelType
}

const useCustomerRecentTasksDashboardLogic = ({ allRowData }: CustomerRecentTasksDashboardLogicProps) => {
    const [data, setData] = useState<any | null>(null)
    const [projects, setProjects] = useState<TwinDictionary | null>(null)
    const listenSocketStr = 'getAllTasksCustomer_' + allRowData?.id
    
    const getData = useCallback(async () => {
        const result = await twinFetchPostJSON('/api/app/task/getAllTasks', {
            limit: 30,
            offset: 0,
            orderToParse: [["id", "desc"]
            ],
            whereToParse: {
                'CustomerProject.CustomerId': allRowData?.id,
                assigned: 1,
                deletedF: false,
                subtask: 1,
                visibles: 2
            }
        })
        if (result) {
            setData(result)
        }
    }, [setData, allRowData?.id])

    const getCustomerProjects = useCallback(async () => {
        const result = await twinFetchPostJSON('/api/app/customer/customerProject/getAllCustomerProjects', { where: { CustomerId: allRowData?.id } })
        if (result) {
            setProjects(dictionaryComplexFromJsonArr(result))
        }
    }, [setProjects, allRowData?.id])

    useEffect(() => {
        getData()
        getCustomerProjects()
    }, [getData, getCustomerProjects])

    useEffect(() => {
        listenSocket(listenSocketStr, getData, true)
        return () => {
            unlistenSocket(listenSocketStr)
        }
    }, [getData, listenSocketStr])

    return { data, getData, projects }
}

type AddTaskButtonProps = & AddTaskButtonLogicProps & ComponentWithPermissions & { }

const AddTaskButton: React.FC<AddTaskButtonProps> = ({ onSubmit, CustomerProjectId, userPermissions }) => {
    const { createTask } = useAddTaskButtonLogic({CustomerProjectId, onSubmit})
    return (
        <PermissionChecker permission='create' userPermissions={userPermissions}>
            <div className='flex items-center text-green-21 cursor-pointer' onClick={createTask}>
                <TwinIcon icon={faPlus} className='mr-4 regular12' />
                <TwinTrans transKey='newTask'>Nueva tarea</TwinTrans>
            </div>
        </PermissionChecker>
    )
}

interface AddTaskButtonLogicProps {
    CustomerProjectId: string
    onSubmit: OnSubmit
}

const useAddTaskButtonLogic = ({ CustomerProjectId, onSubmit }: AddTaskButtonLogicProps) => {
    const user = getUserInfo()
    const createTask = useCallback(async () => {
        let WorkingCenterId = undefined
        const workingCenterByDefault = getConfigParam('assignTaskYourWorkingCenterByDefault')
        if (workingCenterByDefault && workingCenterByDefault === 'on') {
            WorkingCenterId = user?.WorkingCenterId
        }

        const createdTask = await twinFetchPostJSON('/api/app/task/createTask', { WorkingCenterId, CustomerProjectId })
        if (createdTask) {
            onSubmit(createdTask, {})
            addBottomNavAppElement({ type: 'taskModal', key: 'taskModal-' + createdTask.id, extraData: { id: createdTask.id, opened: true } })
        }
    }, [user?.WorkingCenterId, CustomerProjectId, onSubmit])

    return {createTask}
}

interface CustomerTasksListProps {
    tasks: TaskModelType[]
    customerType: CustomerTypesInversedKeys
 }

const CustomerTasksList: React.FC<CustomerTasksListProps> = ({ tasks, customerType }: CustomerTasksListProps) => {
    const { t } = useTwinTranslation()
    const renderThis: JSX.Element[] = []
    if (tasks) {
        for (const task of tasks) {
            renderThis.push(<SingleTask task={task} key={task.id} />)
        }
    }
    return (
        <div className='flex flex-col flex-auto h-1 gap-y-15'>
            <div className='flex flex-col flex-auto h-1 overflow-auto pr-10 items-center'>
                {renderThis.length ? renderThis : <span className='italic text-gray-9D pl-1 mt-30'>{t('noTasks', 'No hay tareas')}</span>}
            </div>
            <CustomerDashboardSeeAllButton customerType={customerType} customerTab='tasks' text={t('SeeAllTasks', 'Ver todas las tareas')} />
        </div>
    )
}

interface SingleTaskProps {
    task: TaskModelType
 }

const SingleTask: React.FC<SingleTaskProps> = ({ task }) => {
    const { t } = useTwinTranslation()
    const taskName = task?.name || t('title', 'Título')
    const hiddenIconMessage = t('notShowUntilDate', 'No mostrar hasta')
    const isTaskVisible = task?.remember_date && new Date() >= new Date(task?.remember_date)
    return (
        <div className='flex w-full items-center border-b border-gray-EE gap-x-15 py-3'>
            <div>
                <CustomRenderTasksFinished value={String(task?.finished) === 'true'} allRowData={task} width={20} />
            </div>
            <div className='flex flex-auto w-1 justify-between cursor-pointer' onClick={() => addBottomNavAppElement({ type: 'taskModal', key: 'taskModal-' + task?.id, extraData: { id: task?.id, opened: true } })}>
                <div className={'flex min-w-130 items-center twin_elipsis ' + (!task?.name ? 'text-gray-97' : '')} id={'task' + task?.id}>
                    {taskName}
                </div>
                {task?.remember_date && !isTaskVisible ?
                    <TwinIconToolTip id={'taskInvisible' + task?.id} className='ml-10 text-14 hidden_admin_comment' icon={faEyeSlash} message={hiddenIconMessage + ' ' + displayDate(new Date(task?.remember_date))} />
                    : null }
            </div>
            {task ? <SingleTaskTopData task={task} taskName={taskName} /> : null}
        </div>
    )
}

interface SingleTaskTopDataProps {
    task: TaskModelType
    taskName: string
}

const SingleTaskTopData: React.FC<SingleTaskTopDataProps> = ({ task, taskName }) => {
    return (
        <TwinToolTip place='top' anchorSelect={'#task' + task?.id} className='max-w-300'>
            <div>
                <div>#{task?.id}</div>
                <div className={'twin_two_lines_elipsis ' + (!task?.name ? 'text-gray-97' : '')}>{taskName}</div>
                <CustomSelectTaskContactInfoRow icon={faCalendar} text={displayDateTime(new Date(task?.createdAt))} />
                <CustomSelectTaskContactInfoRow icon={faInfoCircle} text={task?.description} />
            </div>
        </TwinToolTip>
    )
}

export default CustomerRecentTasksDashboard
